import { Navigate } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { useAuthStore } from '../stores/auth'
import { useNotificationStore } from '../stores/notification'
import { generateMockNotifications } from '../mock/seeds/notifications'
import { Bell, Check, Trash2, Filter } from 'lucide-react'
import { format } from 'date-fns'
import type { NotificationType } from '../types/notification'

const NOTIFICATION_ICONS: Record<NotificationType, string> = {
  order_placed: '📦',
  order_confirmed: '✓',
  order_packed: '📦',
  order_shipped: '🚚',
  order_out_for_delivery: '🚚',
  order_delivered: '✓',
  order_cancelled: '❌',
  stock_alert: '🔔',
  promotional: '🎉',
  support_reply: '💬',
}

export default function NotificationsPage() {
  const { isAuthenticated } = useAuthStore()
  const { notifications, addNotification, markAsRead, markAllAsRead, clearAll } = useNotificationStore()
  const [filter, setFilter] = useState<'all' | 'unread'>('all')

  // Initialize with mock notifications
  useEffect(() => {
    if (notifications.length === 0) {
      const mockNotifications = generateMockNotifications(20)
      mockNotifications.forEach((notif) => {
        addNotification({
          type: notif.type,
          title: notif.title,
          message: notif.message,
          link: notif.link,
        })
      })
    }
  }, [addNotification, notifications.length])

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  const filteredNotifications =
    filter === 'unread' ? notifications.filter((n) => !n.is_read) : notifications

  const unreadCount = notifications.filter((n) => !n.is_read).length

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold mb-2">Notifications</h1>
            <p className="text-gray-600">
              You have {unreadCount} unread notification{unreadCount !== 1 ? 's' : ''}
            </p>
          </div>
          {notifications.length > 0 && (
            <div className="flex gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <Check className="w-4 h-4" />
                  Mark all read
                </button>
              )}
              <button
                onClick={clearAll}
                className="flex items-center gap-2 px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors"
              >
                <Trash2 className="w-4 h-4" />
                Clear all
              </button>
            </div>
          )}
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-gray-500" />
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              filter === 'all'
                ? 'bg-blue-100 text-blue-700 border border-blue-300'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            All ({notifications.length})
          </button>
          <button
            onClick={() => setFilter('unread')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              filter === 'unread'
                ? 'bg-blue-100 text-blue-700 border border-blue-300'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Unread ({unreadCount})
          </button>
        </div>
      </div>

      {/* Notifications List */}
      {filteredNotifications.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-lg p-12 text-center">
          <Bell className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold mb-2">No notifications</h3>
          <p className="text-gray-600">
            {filter === 'unread' ? 'All caught up! No unread notifications.' : "You don't have any notifications yet."}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {filteredNotifications.map((notification) => (
            <div
              key={notification.id}
              className={`bg-white border border-gray-200 rounded-lg p-4 transition-all ${
                !notification.is_read ? 'bg-blue-50 border-blue-200' : ''
              }`}
            >
              <div className="flex gap-4">
                <div className="text-2xl flex-shrink-0">{NOTIFICATION_ICONS[notification.type]}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <h3 className="font-semibold">{notification.title}</h3>
                    {!notification.is_read && (
                      <button
                        onClick={() => markAsRead(notification.id)}
                        className="flex-shrink-0 text-sm text-blue-600 hover:underline"
                      >
                        Mark read
                      </button>
                    )}
                  </div>
                  <p className="text-gray-700 mb-2">{notification.message}</p>
                  <p className="text-sm text-gray-500">{format(new Date(notification.created_at), 'MMM dd, yyyy • hh:mm a')}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
